import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import { getRightworkAPI } from "../../requests/utilities/requests";
import { API_get_plan_task_timeline } from "../../../../constants";

// The history of one task occurrence, rendered the way the ticket history
// tooltip renders its entries: dot, connector, action, time, "by user",
// bullet changes. Comments are entries too, with the chat icon and the text
// as the body, so the timeline reads as one story.

export function useTaskTimeline({ storeId, occurrenceUuid, occurrenceDate, enabled = true, wholeDay = false }) {
  const [timeline, setTimeline] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const seqRef = React.useRef(0);

  const fetchTimeline = React.useCallback(async () => {
    if (!storeId || !occurrenceUuid) return;
    const seq = ++seqRef.current;
    setLoading(true);
    try {
      const res = await getRightworkAPI(API_get_plan_task_timeline, {
        store_id: storeId,
        plan_task_assignment_uuid: occurrenceUuid,
        specific_date: occurrenceDate,
        ...(wholeDay ? { whole_day: "true" } : {}),
      });
      if (seqRef.current !== seq) return;
      setTimeline(res.data?.redux?.payload?.plan_task_timeline || []);
    } catch (error) {
      if (seqRef.current !== seq) return;
      console.error("Error fetching task timeline:", error);
      setTimeline([]);
    } finally {
      if (seqRef.current === seq) setLoading(false);
    }
  }, [storeId, occurrenceUuid, occurrenceDate, wholeDay]);

  // Refetch whenever the surface shows it: transitions elsewhere change it.
  React.useEffect(() => {
    if (!enabled) return;
    setTimeline(null);
    fetchTimeline();
  }, [fetchTimeline, enabled]);

  return { timeline, loading, refresh: fetchTimeline };
}

// Times read in the STORE's zone (the payloads carry it), like every other
// time on these surfaces; the device zone is only the fallback.
export const formatTimelineTimestamp = (isoString, tz) => {
  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: tz || undefined,
  });
};

export function TimelineList({ timeline, loading, tz, compact = false }) {
  if (loading || timeline === null) {
    return (
      <div className="flex items-center justify-center py-4">
        <CircularProgress size={20} sx={{ color: "#6d28d9" }} />
      </div>
    );
  }
  if (!timeline.length) {
    return <p className="text-xs text-slate-500">No history yet</p>;
  }
  return (
    <div className="space-y-4">
      {timeline.map((item, index) => {
        const isComment = item.kind === "comment";
        return (
          <div key={index} className="relative">
            {index !== timeline.length - 1 && (
              <div className="absolute left-1 top-6 bottom-0 w-0.5 bg-slate-200"></div>
            )}
            <div className="flex items-start gap-3">
              <div
                className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${
                  isComment ? "bg-slate-400" : "bg-violet-700"
                }`}
              ></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1 gap-2">
                  <p className={`${compact ? "text-xs" : "text-sm"} font-semibold text-slate-900 flex items-center gap-1.5`}>
                    {isComment && <ChatOutlinedIcon style={{ fontSize: "14px" }} className="text-slate-400" />}
                    {item.action}
                    {item.slot && (
                      <span className="text-[10px] font-semibold rounded-full px-1.5 py-px bg-slate-100 text-slate-500">
                        {item.slot}
                      </span>
                    )}
                  </p>
                  <p className="text-xxs text-slate-500 whitespace-nowrap">
                    {formatTimelineTimestamp(item.timestamp, tz)}
                  </p>
                </div>
                <p className="text-xxs text-slate-600 mb-1">by {item.user}</p>
                {isComment ? (
                  <p className={`${compact ? "text-xs" : "text-sm"} text-slate-700 whitespace-pre-wrap`}>
                    {item.changes?.[0]?.new_value}
                  </p>
                ) : (
                  item.changes?.length > 0 && (
                    <div className="text-xxs text-slate-500 space-y-0.5">
                      {item.changes.map((change, i) => (
                        <div key={i} className="flex items-start">
                          <span className="mr-1">•</span>
                          <span>
                            {change.field
                              ? `${change.field}: ${change.new_value}`
                              : change.new_value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function TaskTimeline({ storeId, occurrenceUuid, occurrenceDate, tz, wholeDay = false }) {
  const { timeline, loading } = useTaskTimeline({ storeId, occurrenceUuid, occurrenceDate, wholeDay });
  return <TimelineList timeline={timeline} loading={loading} tz={tz} />;
}
